import { useProductSoldSummary } from '~/composables/useProductSoldSummary'

type SumProductTypeRow = {
  name?: string
  groupByType?: Record<string, any>
}

export function buildProductSummaryByTypeFromSumProductType(
  sumProductType: SumProductTypeRow[] = [],
  options: { dropZeroCatalogs?: boolean; dropZeroSubItems?: boolean } = {},
) {
  const grouped = Array.isArray(sumProductType) ? sumProductType : []

  const productSoldSummary = grouped
    .filter((g) => g?.groupByType && g?.name)
    .reduce((acc: any, { name, groupByType }: any) => {
      const bucket = acc[name] ?? (acc[name] = {})
      for (const [k, v] of Object.entries(groupByType || {})) {
        ;(bucket[k] ??= []).push(v as any)
      }
      return acc
    }, {})

  const { summarizeAmountByTypeFromSubItems } = useProductSoldSummary()
  return summarizeAmountByTypeFromSubItems(productSoldSummary, options)
}

export function mapSummaryByTypeToRows(summaryByType: Record<string, any> = {}) {
  return Object.entries(summaryByType).map(([typeName, bucket]: any) => ({
    label: titleCase(typeName),
    value: Number(bucket?.totalAmount || 0),
  }))
}

export function mapSummaryByTypeToDetailRows(summaryByType: Record<string, any> = {}) {
  return Object.entries(summaryByType).flatMap(([typeName, bucket]: any) =>
    (bucket?.items || []).map((catalog: any) => {
      // Group subItems by their parent item (itemKey/itemName) so the row formatter
      // can decide whether to suppress the "(subName)" qualifier (Regular single-sub).
      const itemMap = new Map<string, { itemName: string; subItems: Array<{ subName: string; qty: number; amount: number }> }>()
      for (const sub of catalog?.subItems || []) {
        const itemName = String(sub?.itemName || catalog?.name || '-')
        const key = String(sub?.itemKey || itemName)
        const bucketItem = itemMap.get(key) ?? { itemName, subItems: [] }
        bucketItem.subItems.push({
          subName: String(sub?.subName || sub?.name || ''),
          qty: Number(sub?.quantity || 0),
          amount: Number(sub?.amount || 0),
        })
        itemMap.set(key, bucketItem)
      }

      const items = Array.from(itemMap.values())

      // Back-compat: keep a flat `subs` so older consumers still work; populate name
      // using the same (subName) suppression rule as the thermal formatter.
      const flatSubs = items.flatMap(({ itemName, subItems }) => {
        const realSubs = subItems.filter((s) => Number(s.qty) > 0)
        const onlySub = realSubs.length === 1 ? realSubs[0] : null
        const hideSubLabel = !!onlySub && (onlySub.subName || '').trim().toLowerCase() === 'regular'
        return realSubs.map((s) => ({
          name: hideSubLabel ? itemName : `${itemName} (${s.subName})`,
          itemName,
          subName: s.subName,
          qty: s.qty,
          amount: s.amount,
        }))
      })

      return {
        typeName: titleCase(typeName),
        productName: String(catalog?.name || '-'),
        total: Number(catalog?.totalamount || 0),
        items,
        subs: flatSubs,
      }
    }),
  )
}

function titleCase(value: string) {
  const lower = String(value || '').toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}
